import { defineResource } from '@tnn/core'
import { z } from 'zod'
import { type PaySide, paySides, type WaybillValues, waybillOwed } from './waybill'

/**
 * Receipt (ใบเสร็จ) for freight paid at a branch. One receipt can pay several waybills at once, each for what
 * its payer owes there (`lines`: the sender at the origin, the receiver at the destination), with the money
 * taken in several parts (two transfers, a transfer and cash…); what was taken beyond the freight is given
 * back as cash change or a refund transfer. Taking payment (`POST /api/tms/receipts/pay`) creates and submits one;
 * submitting numbers it per branch and marks its waybills paid, cancelling (with a reason)
 * makes them unpaid again. Freight is exempt from VAT: no tax invoice.
 *
 * Who paid (`payerName`): a company also by its tax ID, branch and address (`payerTaxId`, `payer*`), printed on the
 * receipt so it needs no other one; filled in from its contact (`payerDefaults`), a person's name only. One made out
 * again later, or with the details wrong, is accounting's receipt in the customer's name (accounting.customerReceipt,
 * `customerReceiptId` while it stands), which then keeps this one's money and payer.
 *
 * A juristic payer may withhold 1% of the freight (หัก ณ ที่จ่าย, `withheld`, with its `payerTaxId`): the money makes up
 * the rest, the bills are paid in full. Its certificate (50 ทวิ) may come later: `whtStatus` pending until its photo is
 * attached.
 *
 * Whoever records it took the money and holds it, at their own branch — but someone at head office may record a branch's
 * money for its person who took it and does not use the app: the receipt is then that branch's, the cash in their hands
 * (`collectedBy`, `collectedByUserId`), with the day the customer paid (`paidOn`, up to `receiptBackdateDays` back).
 * It may carry the number of a handwritten receipt the customer got (`manualNo`).
 */
export const receiptStatuses = ['draft', 'issued', 'cancelled'] as const
/** How many days back a receipt recorded for someone else may say the customer paid (`paidOn`) */
export const receiptBackdateDays = 7
/** How one part of the money came in, or how the excess went back */
export const paymentMethods = ['cash', 'transfer'] as const
export type PaymentMethod = (typeof paymentMethods)[number]
/** The receipt as a whole, for lists and filters: one method, or `mixed` */
export const receiptMethods = ['cash', 'transfer', 'mixed'] as const
export type ReceiptMethod = (typeof receiptMethods)[number]
/** A withholding certificate (50 ทวิ): still to come from the customer, or its photo attached */
export const whtCertStatuses = ['pending', 'received'] as const
export type WhtCertStatus = (typeof whtCertStatuses)[number]

/** A juristic person's 13-digit tax ID (`isJuristicTaxId`): only one withholds tax on freight, or is a receipt's company */
export const juristicTaxIdSchema = z.string().regex(/^0\d{12}$/, 'นิติบุคคลเท่านั้น: เลขผู้เสียภาษี 13 หลัก ขึ้นต้นด้วย 0 (บุคคลธรรมดาไม่ใส่)')

/** A receipt's company details, besides `payerName` and `payerTaxId`: kept only with a tax ID */
export const payerDetailFields = [
  'payerBranch',
  'payerAddress',
  'payerSubdistrict',
  'payerDistrict',
  'payerProvince',
  'payerPostcode',
] as const
/** Everything a receipt says of who paid: what a receipt in the customer's name standing for it holds still */
export const payerFields = ['payerName', 'payerTaxId', ...payerDetailFields] as const

export const receiptPaymentSchema = z.object({
  method: z.enum(paymentMethods),
  amount: z.number().positive(),
  /** Transfers: the bank or slip reference, to match the statement */
  reference: z.string().trim().max(100).nullish(),
  /** Transfers: a photo of the customer's slip (file id), optional */
  slipId: z.string().nullish(),
})
export type ReceiptPayment = z.infer<typeof receiptPaymentSchema>

/** Partial payment: take less than a bill owes at the branch (more than 0, at most what it owes there) */
export const receiptAmountSchema = z.object({
  waybillId: z.string().min(1),
  amount: z.number().positive(),
})
export type ReceiptAmount = z.infer<typeof receiptAmountSchema>

/** What a receipt took for one bill from one party (set by the server from `payDue` and `amounts`) */
export const receiptLineSchema = z.object({
  waybillId: z.string().min(1),
  side: z.enum(paySides),
  amount: z.number().min(0),
})
export type ReceiptLine = z.infer<typeof receiptLineSchema>

export const receipt = defineResource({
  app: 'tms',
  name: 'receipt',
  path: 'receipts',
  schema: z.object({
    branchId: z.string().min(1),
    waybillIds: z.array(z.string()).min(1),
    /** Bills paid in part, and how much of each; every other bill is paid in full (what it owes at the branch) */
    amounts: z.array(receiptAmountSchema).default([]),
    payments: z.array(receiptPaymentSchema).min(1),
    /** How the excess (paid minus freight) goes back: cash change or a refund transfer */
    refundMethod: z.enum(paymentMethods).default('cash'),
    /** Money the company pays out needs proof: the transfer reference… */
    refundReference: z.string().trim().max(100).nullish(),
    /** …or a photo of the refund slip (file id); see `refundNeedsProof` */
    refundSlipId: z.string().nullish(),
    payerName: z.string().trim().max(200).nullish(),
    /** A company's tax ID: the receipt is made out to the company (the one on its 50 ทวิ when it withholds) */
    payerTaxId: juristicTaxIdSchema.nullish(),
    /** A company's branch (00000 = head office) and address */
    payerBranch: z.string().trim().max(10).nullish(),
    payerAddress: z.string().trim().max(500).nullish(),
    payerSubdistrict: z.string().trim().max(100).nullish(),
    payerDistrict: z.string().trim().max(100).nullish(),
    payerProvince: z.string().trim().max(100).nullish(),
    payerPostcode: z.string().trim().max(10).nullish(),
    /** The customer withheld 1% of the freight (needs `payerTaxId`) */
    withheld: z.boolean().default(false),
    /** The certificate's book and number, and a photo of it (file id): may come later */
    whtCertNo: z.string().trim().max(50).nullish(),
    whtCertFileId: z.string().nullish(),
    /** The number of a handwritten receipt the customer got */
    manualNo: z.string().trim().max(50).nullish(),
    notes: z.string().max(500).nullish(),
  }),
  fields: {
    number: { readOnly: true, search: true, list: { width: 150, priority: 1 } },
    issuedAt: { readOnly: true, type: 'datetime', filter: true, list: { width: 160, priority: 1 } },
    /** The day the customer paid: the day it is issued (earlier only on receipts recorded for someone, before) */
    paidOn: { readOnly: true, filter: true, list: { width: 130, priority: 3, hidden: true } },
    manualNo: { search: true, list: { width: 130, priority: 3, hidden: true }, form: { span: 6 } },
    branchId: { type: 'ref', ref: { resource: 'company.branch' }, filter: true, list: { priority: 2 }, form: { span: 6 } },
    payerName: { search: true, list: { priority: 1 }, form: { span: 12, section: 'payer' } },
    // A company's details, taken in the pay dialog; corrected after by a lead or accounting ("edit after submit")
    payerTaxId: { type: 'taxId', search: true, list: { priority: 3, hidden: true }, form: { span: 6, section: 'payer' } },
    payerBranch: { list: false, form: { span: 6, section: 'payer' } },
    payerAddress: { type: 'textarea', list: false, form: { span: 12, section: 'payer' } },
    payerProvince: { list: false, form: { span: 12, section: 'payer', custom: true } },
    payerDistrict: { list: false, form: { hidden: true } },
    payerSubdistrict: { list: false, form: { hidden: true } },
    payerPostcode: { list: false, form: { hidden: true } },
    /** Who held the money, on receipts recorded for them before (no longer done) */
    collectedBy: { readOnly: true, search: true, list: { priority: 3, hidden: true } },
    collectedByUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, list: false, import: false },
    waybillIds: { type: 'multiselect', ref: { resource: 'tms.waybill' }, list: { priority: 3, hidden: true }, form: { span: 12 } },
    amounts: { type: 'json', list: false, form: { hidden: true }, import: false, export: false },
    /** Per bill and party, what this receipt took (server-managed) */
    lines: { readOnly: true, type: 'json', list: false, export: false },
    payments: { type: 'json', list: false, form: { span: 12, section: 'payments', custom: true }, import: false },
    refundMethod: { list: false, form: { span: 4, section: 'payments' } },
    refundReference: { list: false, form: { span: 4, section: 'payments' } },
    refundSlipId: { type: 'file', list: false, form: { span: 4, section: 'payments' }, import: false, export: false },
    /** Summary of `payments`: cash, transfer or mixed (set by the server) */
    method: { readOnly: true, type: 'select', options: receiptMethods, filter: true, list: { width: 120, priority: 2 } },
    /** What the bills' parties owed here: the sum of `lines` */
    amount: { readOnly: true, type: 'money', list: { width: 130, align: 'end', priority: 1 } },
    /** Everything taken in, all parts together */
    received: { readOnly: true, type: 'money', list: { align: 'end', priority: 3, hidden: true } },
    /** Given back: received minus amount */
    refund: { readOnly: true, type: 'money', list: { align: 'end', priority: 3, hidden: true } },
    status: {
      readOnly: true,
      type: 'select',
      options: receiptStatuses,
      filter: true,
      tones: { draft: 'neutral', issued: 'success', cancelled: 'danger' },
      list: { width: 120, priority: 1 },
    },
    issuedBy: { readOnly: true, list: { priority: 3, hidden: true } },
    // Taken in the pay dialog; shown, and its 50 ทวิ attached, beside the receipt
    withheld: { list: false, form: { hidden: true } },
    /** 1% of `amount` when withheld (set by the server) */
    whtAmount: { readOnly: true, type: 'money', list: { align: 'end', priority: 3, hidden: true } },
    whtCertNo: { search: true, list: false, form: { hidden: true } },
    whtCertFileId: { type: 'file', list: false, form: { hidden: true }, import: false, export: false },
    /** Withheld: its 50 ทวิ still to come, or attached (set by the server) */
    whtStatus: {
      readOnly: true,
      type: 'select',
      options: whtCertStatuses,
      filter: true,
      tones: { pending: 'warning', received: 'success' },
      list: { width: 140, priority: 3, hidden: true },
    },
    notes: { type: 'textarea', list: false, form: { span: 12, section: 'other' } },
    /** The receipt in the customer's name issued in its place, while it stands (set by the server) */
    customerReceiptId: { readOnly: true, type: 'ref', ref: { resource: 'accounting.customerReceipt' }, list: false, import: false },
  },
  title: 'number',
  columns: ['number', 'issuedAt', 'payerName', 'branchId', 'method', 'amount', 'status', 'received', 'refund', 'waybillIds', 'issuedBy'],
  sections: ['main', 'payer', 'payments', 'other'],
  defaultSort: '-issuedAt',
  actions: ['read', 'update', 'delete', 'export', 'print'],
  branchFields: ['branchId'],
  submittable: { cancelReason: 'required' },
  card: { title: 'number', subtitle: 'payerName', meta: ['issuedAt', 'amount'], status: 'status' },
})

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100

export interface ReceiptTotals {
  /** The waybills' freight */
  amount: number
  /** Withheld by the customer (1% of the freight, `freightWht`), when they withhold */
  wht: number
  /** Taken in, all parts */
  received: number
  /** To give back (received beyond the freight less what was withheld) */
  refund: number
  /** Still to take before the receipt can be issued */
  short: number
}

/** Freight a juristic person pays is withheld 1% (ค่าขนส่ง): of what the receipt takes */
export const freightWhtRate = 1
export const freightWht = (amount: number) => round2((amount * freightWhtRate) / 100)

/** The most freight `net` money settles when 1% of it is withheld: its freight less `freightWht` is no more than `net` */
export function freightGross(net: number): number {
  let gross = round2((net * 100) / (100 - freightWhtRate))
  while (gross > 0 && round2(gross - freightWht(gross)) > net) gross = round2(gross - 0.01)
  return gross
}

/**
 * Same numbers on the pay dialog (live) and the API (stored); a receipt is issued only when `short` is 0. `withheld`:
 * the customer withholds 1%, which settles that much of the freight with no money
 */
export function receiptTotals(
  fees: readonly number[],
  payments: readonly Pick<ReceiptPayment, 'amount'>[],
  withheld = false,
): ReceiptTotals {
  const amount = round2(fees.reduce((sum, fee) => sum + (fee || 0), 0))
  const wht = withheld ? freightWht(amount) : 0
  const due = round2(amount - wht)
  const received = round2(payments.reduce((sum, p) => sum + (p.amount || 0), 0))
  return { amount, wht, received, refund: round2(Math.max(received - due, 0)), short: round2(Math.max(due - received, 0)) }
}

/** One method when every part used it, else `mixed` */
export function receiptMethodOf(payments: readonly Pick<ReceiptPayment, 'method'>[]): ReceiptMethod {
  const methods = new Set(payments.map((p) => p.method))
  return methods.size === 1 ? ([...methods][0] as ReceiptMethod) : 'mixed'
}

/**
 * Money the company pays out needs a reference or a slip photo: a refund transfer, or cash
 * given back beyond the cash the customer handed over (an overpaid transfer refunded in
 * cash). Ordinary change from cash needs none.
 */
export function refundNeedsProof(
  payments: readonly Pick<ReceiptPayment, 'method' | 'amount'>[],
  refund: number,
  refundMethod: PaymentMethod,
): boolean {
  if (refund <= 0) return false
  if (refundMethod === 'transfer') return true
  const cashIn = payments.filter((p) => p.method === 'cash').reduce((sum, p) => sum + (p.amount || 0), 0)
  return refund > round2(cashIn)
}

/**
 * Whether a branch can take a bill's freight: `ok`; `switch` (after changing who pays); `paidPart` (it would have to
 * switch, but the payer already paid part: they pay the rest at their end); `elsewhere` (another branch's)
 */
export type PayCheck = 'ok' | 'switch' | 'paidPart' | 'elsewhere'

/** A waybill as `payDue` reads it: its payment fields (`WaybillValues`) and its two ends, originBranchId and destBranchId */
export type PayBill = WaybillValues

/** What a branch takes for a bill: whether it may, from which parties, and how much */
export interface PayDue {
  check: PayCheck
  sides: PaySide[]
  amount: number
}

/**
 * Where a waybill's freight is taken: the sender's at its origin branch, the receiver's
 * (freight collect) at its destination, also in advance. A branch
 * that is one end of the bill but whose party owes nothing there can take what the other party
 * still owes after switching who pays (`switchedPayer`, logged as an edit after submit) — unless the payer already
 * paid part (`paidPart`: switching would turn what they paid into a refund, so they pay the rest at their end);
 * another branch's bill is `elsewhere`. Head office (`hq`) takes everything still owed.
 */
export function payDue(bill: PayBill, branch: { id: string; type?: string | null }): PayDue {
  const owed = waybillOwed(bill)
  const owing = paySides.filter((side) => owed[side] > 0)
  const due = (sides: PaySide[]) => ({ sides, amount: round2(sides.reduce((sum, side) => sum + owed[side], 0)) })
  if (branch.type === 'hq') return { check: 'ok', ...due(owing) }
  const here = paySides.filter((side) => (side === 'sender' ? bill.originBranchId : bill.destBranchId) === branch.id)
  const ours = here.filter((side) => owing.includes(side))
  if (ours.length) return { check: 'ok', ...due(ours) }
  if (here.length && owing.length) {
    const paidPart = owing.some((side) => (Number(bill[`${side}Paid`]) || 0) > 0)
    return paidPart ? { check: 'paidPart', sides: [], amount: 0 } : { check: 'switch', ...due(owing) }
  }
  return { check: 'elsewhere', sides: [], amount: 0 }
}

/** `payDue`'s verdict alone */
export function payCheck(bill: PayBill, branch: { id: string; type?: string | null }): PayCheck {
  return payDue(bill, branch).check
}

/** Who pays once a cashier at one end switches a bill to take it there: that end's party pays everything still owed */
export function switchedPayer(bill: PayBill, branch: { id: string }): PaySide {
  return bill.originBranchId === branch.id ? 'sender' : 'receiver'
}
